/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { UIState, ConfigState, LibraryData, ProductState } from '../store/interfaces';
 
import styles from '../styles/finalscreen.module.scss'
import LanguageSwitcherContainer from '../containers/LanguageSwitcherContainer';
import { LogoText } from './LogoText';

import {ReactComponent as IconArrowLeft} from '../icons/arrow_left.svg';
import {ReactComponent as IconCheckmark} from '../icons/checkmark.svg';

import { getFinalPricingNoVat, getPricingTextLong, getShippingPrice, addPrice, applyVat, getVat } from '../helpers/pricing';
import { Fragment, useRef, useState } from 'react';
import { Progress } from './Progress';
import CurrentConfigContainer from '../containers/CurrentConfigContainer';
import { Product } from './Product';
import { isMobile } from 'react-device-detect';
import { PreviewContainer, PreviewContainerModel } from './Preview';
import { GetEULALink } from '../helpers/helpers';
import LinkFooterContainer from '../containers/LinkFooterContainer';


interface FinalscreenProps {
    ui: UIState;
    config: ConfigState;
    library: LibraryData;    
    eventRef?: React.MutableRefObject<HTMLDivElement>;
    scrollRef?: React.MutableRefObject<HTMLDivElement>;
    onUiGoTo: (route: string) => void;
    onUiSetMobileOrderStep: (step: string) => void;
    onConfigSetBasics: (basics: boolean) => void;
    onConfigSend: (lang: string) => void;
    onProductUpdate: (index: number, state: ProductState) => void;
    onProductRemove: (index: number) => void;
}

export function Finalscreen(props: FinalscreenProps) {
    const {t, i18n} = useTranslation();

    const eventRef = useRef<HTMLDivElement>(null!);
    const scrollRef = useRef<HTMLDivElement>(null!);
    const canvasRef = useRef<HTMLCanvasElement>(null!);

    const divRefs = [
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!)
    ];

    const [isPolicyChecked, setIsPolicyChecked] = useState(false);

    let totalAmountNoVat = getFinalPricingNoVat(props.library, props.config.products);
    let shippingPrice = getShippingPrice(props.library, totalAmountNoVat);
    let totalVat = getVat(totalAmountNoVat, props.ui.country.vat);
    let totalAmountVat = addPrice(applyVat(totalAmountNoVat, props.ui.country.vat), shippingPrice);

    // Note(daniel): Needed to correctly align canvas views after the sidebar animation.
    setTimeout(() => {
        if(canvasRef.current) {
            window.dispatchEvent(new Event('resize'));
            canvasRef.current.parentElement?.parentElement?.classList.add(styles.visible);
        }
    }, 1100);
    
    return (
        <Container fluid className={styles.container} ref={scrollRef}>
            <div className={styles.content} ref={eventRef}>
                <PreviewContainer
                    className={styles.fullCanvas}
                    eventRef={props.eventRef !== undefined ? props.eventRef : eventRef}
                    scrollRef={props.scrollRef !== undefined ? props.scrollRef : scrollRef}
                    canvasRef={canvasRef}
                >
                    {
                        props.config.products.map((product, index) => {
                            let article = props.library.products.find((article) => {
                                return article.article === product.article;
                            });
                            if(article === null || article === undefined) {
                                return null;
                            }
                            return <PreviewContainerModel divRef={divRefs[index]} library={props.library} model={article.article} thumbnail={true} modelData={article.modelDataThumbnail !== undefined ? article.modelDataThumbnail : article.modelData} xOffset={0} baseColorId={props.config.baseColorId} contrastColorId={props.config.contrastColorId} straussLogo={props.config.straussLogo} showPlanes={false}/>
                        })
                    }
                </PreviewContainer>
                <div className={styles.header}>
                    <LogoText size={'small'} black={true}/>
                </div>
                <div className={styles.products}>
                    <div className={styles.productsLeft}>
                        <div className={styles.productsHeader}>
                            <button className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiGoTo('customerscreen')}}><IconArrowLeft/>{t('finalscreen.backToCustomerscreen')}</button>
                            <Progress progress={1}/>
                        </div>
                        <h1>{t('finalscreen.header')}</h1>
                        <p>{t('finalscreen.subHeader')}</p>
                        {
                            props.config.products.map((product, index) => {
                                let article = props.library.products.find((article) => {
                                    return article.article === product.article;
                                });
                                if(article === null || article === undefined) {
                                    return null;
                                }
                                return <Product divRef={divRefs[index]} library={props.library} config={props.config} country={props.ui.country} product={product} article={article} index={index} key={index} vat={props.ui.country.vat} protected={true}/>
                            })
                        }
                        <div className={styles.productSumContainer}>
                            <div className={styles.productSum}>
                                <div className={`${styles.productColumns} ${styles.borderBottom}`}>
                                    <div className={styles.col0}>
                                        <span className={styles.sumBold}>{t('orderscreen.sum.totalNoVat')}</span>
                                    </div>
                                    <div className={styles.col1}>
                                        <span className={styles.sumBold}>{getPricingTextLong(i18n.language, props.ui.country, totalAmountNoVat)}</span>
                                    </div>
                                    <div className={styles.col0}>
                                        <span>{t('orderscreen.sum.shipping')}</span>
                                    </div>
                                    <div className={styles.col1}>
                                        <span>{t('orderscreen.sum.free')}</span>
                                    </div>
                                    <div className={styles.col0}>
                                        <span>{t('orderscreen.sum.additionalVat')}</span>
                                    </div>
                                    <div className={styles.col1}>
                                        <span>{getPricingTextLong(i18n.language, props.ui.country, totalVat)}</span>
                                    </div>
                                </div>
                                <div className={styles.productColumns}>
                                    <div className={styles.col0}>
                                        <span className={styles.sumTotal}>{t('orderscreen.sum.totalWithVat')}</span>
                                    </div>
                                    <div className={styles.col1}>
                                        <span className={styles.sumTotalBold}>{getPricingTextLong(i18n.language, props.ui.country, totalAmountVat)}</span>
                                    </div>
                                </div>
                                <span>{t('orderscreen.sum.withVat')}</span>
                            </div>
                        </div>
                        <h1>{t('finalscreen.furtherActions.header')}</h1>
                        <div className={styles.furtherStepsContainer}>
                            <div>
                                <div>1</div>
                                <h1>{t('finalscreen.furtherActions.step1Header')}</h1>
                                <p>{t('finalscreen.furtherActions.step1Text')}</p>
                            </div>
                            <div>
                                <div>2</div>
                                <h1>{t('finalscreen.furtherActions.step2Header')}</h1>
                                <p>{t('finalscreen.furtherActions.step2Text')}</p>
                            </div>
                            <div>
                                <div>3</div>
                                <h1>{t('finalscreen.furtherActions.step3Header')}</h1>
                                <p>{t('finalscreen.furtherActions.step3Text')}</p>
                            </div>
                        </div>
                        <div className={styles.policyContainer}>
                            <p dangerouslySetInnerHTML={{__html: t('finalscreen.eula', {link: GetEULALink(props.ui.country)})}}></p>
                            {/*<p dangerouslySetInnerHTML={{__html: t('finalscreen.policy', {link: GetGDPRLink(props.ui.country)})}}></p>*/}
                            <label>
                                <input type="checkbox" checked={isPolicyChecked} onChange={(evt) => {setIsPolicyChecked(evt.target.checked)}}></input>
                                {t('finalscreen.policyAccept')}
                            </label>
                        </div>

                        <div className={styles.fill}></div>
                        
                        {!isMobile &&
                            <div className={styles.footerButtons}>
                                <button className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiGoTo('customerscreen')}}><IconArrowLeft/>{t('finalscreen.backToCustomerscreen')}</button>
                                <p className={styles.error}>{props.ui.sendingConfigError !== null ? t('finalscreen.error') : ''}</p>
                                <button data-testid="mymotion_orderButton" className={`${styles.buttonPrimary}`} disabled={props.ui.isSendingConfig || !isPolicyChecked} onClick={(evt) => {props.onConfigSend(i18n.language)}}>{t('finalscreen.send')}<IconCheckmark/></button>
                            </div>     
                        }
                    </div>      
                    {!isMobile &&
                        <div className={styles.productsRight}>
                            <CurrentConfigContainer/>
                        </div>  
                    }
                    {isMobile &&
                        <Fragment>
                            <p className={`${styles.mobileNotice} ${styles.error}`}>{props.ui.sendingConfigError !== null ? t('finalscreen.error') : ''}</p>
                            <div className={styles.footerButtons}>
                                <button className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiSetMobileOrderStep('customer'); props.onUiGoTo('editscreen')}}><IconArrowLeft/>{t('misc.back')}</button>
                                <button data-testid="mymotion_orderButton" className={`${styles.buttonPrimary}`} disabled={props.ui.isSendingConfig || !isPolicyChecked} onClick={(evt) => {props.onConfigSend(i18n.language)}}>{t('finalscreen.send')}<IconCheckmark/></button>
                            </div>     
                        </Fragment>
                    }
                </div>            
                <div className={styles.footer}><LanguageSwitcherContainer black disableCountryChange/><span className={styles.footerSeparator}></span><LinkFooterContainer fullWidth={true}/></div>
            </div>
        </Container>
    );
};